import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ShieldCheck,
  AlertTriangle,
  Search,
  Download,
  Plus,
  Eye,
  Calendar,
  CheckCircle,
  XCircle,
  Loader2,
  Save,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import complianceService from '../services/complianceService';
import * as XLSX from 'xlsx';

export default function LicenseManagementPage() {
  const [licenses, setLicenses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  const [newLicense, setNewLicense] = useState({
    licenseNumber: '',
    licenseType: '',
    operatorName: '',
    state: '',
    issueDate: '',
    expirationDate: '',
    status: 'Active',
    notes: '',
  });

  useEffect(() => {
    fetchLicenses();
  }, []);

  const fetchLicenses = async () => {
    setIsLoading(true);
    try {
      const data = await complianceService.getLicenses();
      setLicenses(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to fetch licenses');
      setLicenses([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await complianceService.createLicense(newLicense);
      toast.success('License created successfully');
      setIsCreateDialogOpen(false);
      setNewLicense({ licenseNumber: '', licenseType: '', operatorName: '', state: '', issueDate: '', expirationDate: '', status: 'Active', notes: '' });
      fetchLicenses();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to create license');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedLicense?.id) return;
    setIsSaving(true);
    try {
      await complianceService.updateLicense(selectedLicense.id, editData);
      toast.success('License updated successfully');
      setIsEditMode(false);
      fetchLicenses();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to update license');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this license?')) return;
    try {
      await complianceService.deleteLicense(id);
      toast.success('License deleted');
      setIsDialogOpen(false);
      fetchLicenses();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to delete license');
    }
  };

  const handleExport = () => {
    try {
      const exportData = filteredLicenses.map(l => ({
        'License Number': l.licenseNumber,
        'Type': l.licenseType,
        'Operator': l.operatorName,
        'State': l.state,
        'Status': l.status,
        'Issue Date': l.issueDate,
        'Expiry Date': l.expirationDate,
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Licenses');
      XLSX.writeFile(wb, `licenses_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Licenses exported successfully');
    } catch {
      toast.error('Failed to export');
    }
  };

  const viewDetails = (license) => {
    setSelectedLicense(license);
    setEditData({ ...license });
    setIsEditMode(false);
    setIsDialogOpen(true);
  };

  const getDaysUntilExpiry = (expirationDate) => {
    if (!expirationDate) return null;
    const now = new Date();
    const expiry = new Date(expirationDate);
    return Math.round((expiry - now) / (1000 * 60 * 60 * 24));
  };

  const getStatusBadge = (license) => {
    const days = getDaysUntilExpiry(license.expirationDate);
    const status = license.status?.toLowerCase();

    if (status === 'expired' || (days !== null && days < 0)) {
      return <Badge variant="destructive" className="flex items-center gap-1"><XCircle className="h-3 w-3" />Expired</Badge>;
    }
    if (days !== null && days <= 30) {
      return <Badge className="bg-orange-500 text-white flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Expiring Soon</Badge>;
    }
    if (status === 'pending' || status === 'pending renewal') {
      return <Badge className="bg-yellow-500 text-white flex items-center gap-1"><Calendar className="h-3 w-3" />Pending</Badge>;
    }
    return <Badge variant="success" className="flex items-center gap-1"><CheckCircle className="h-3 w-3" />Active</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const filteredLicenses = licenses.filter(l =>
    (l.licenseNumber?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
    (l.operatorName?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
    (l.licenseType?.toLowerCase() ?? '').includes(searchTerm.toLowerCase())
  );

  const summaryCards = [
    { title: 'Total Licenses', value: licenses.length, icon: ShieldCheck, color: 'text-blue-600', bgColor: 'bg-blue-50' },
    { title: 'Active', value: licenses.filter(l => l.status?.toLowerCase() === 'active').length, icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-50' },
    { title: 'Expiring Soon', value: licenses.filter(l => { const d = getDaysUntilExpiry(l.expirationDate); return d !== null && d >= 0 && d <= 30; }).length, icon: AlertTriangle, color: 'text-orange-600', bgColor: 'bg-orange-50' },
    { title: 'Expired', value: licenses.filter(l => { const d = getDaysUntilExpiry(l.expirationDate); return d !== null && d < 0; }).length, icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-50' },
  ];

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">License Management</h1>
            <p className="text-muted-foreground mt-1">Track and manage all operator licenses</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchLicenses}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add License
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {summaryCards.map((card, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{card.title}</p>
                    <p className="text-3xl font-bold mt-2">{card.value}</p>
                  </div>
                  <div className={`p-3 ${card.bgColor} rounded-lg`}>
                    <card.icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* License Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>All Licenses</CardTitle>
                <CardDescription>Complete list of operator licenses ({filteredLicenses.length} records)</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by license number, operator, or type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {filteredLicenses.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <ShieldCheck className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No licenses found. Add your first license to get started.</p>
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>License Number</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Operator</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Issue Date</TableHead>
                      <TableHead>Expiry Date</TableHead>
                      <TableHead>Days Until Expiry</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLicenses.map((license) => {
                      const days = getDaysUntilExpiry(license.expirationDate);
                      return (
                        <TableRow key={license.id}>
                          <TableCell className="font-medium">{license.licenseNumber}</TableCell>
                          <TableCell>{license.licenseType}</TableCell>
                          <TableCell>{license.operatorName}</TableCell>
                          <TableCell>{license.state}</TableCell>
                          <TableCell>{formatDate(license.issueDate)}</TableCell>
                          <TableCell>{formatDate(license.expirationDate)}</TableCell>
                          <TableCell>
                            <span className={days !== null && days <= 30 ? 'text-red-600 font-medium' : ''}>
                              {days === null ? 'N/A' : days > 0 ? `${days} days` : 'Expired'}
                            </span>
                          </TableCell>
                          <TableCell>{getStatusBadge(license)}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="sm" onClick={() => viewDetails(license)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View / Edit License Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>License Details</DialogTitle>
            <DialogDescription>
              {isEditMode ? 'Edit license information' : 'Complete information about the license'}
            </DialogDescription>
          </DialogHeader>
          {selectedLicense && (
            <div className="space-y-4">
              {isEditMode ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>License Number</Label>
                    <Input value={editData.licenseNumber ?? ''} onChange={e => setEditData(p => ({ ...p, licenseNumber: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>License Type</Label>
                    <Input value={editData.licenseType ?? ''} onChange={e => setEditData(p => ({ ...p, licenseType: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Operator Name</Label>
                    <Input value={editData.operatorName ?? ''} onChange={e => setEditData(p => ({ ...p, operatorName: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>State</Label>
                    <Input value={editData.state ?? ''} onChange={e => setEditData(p => ({ ...p, state: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Issue Date</Label>
                    <Input type="date" value={editData.issueDate?.split('T')[0] ?? ''} onChange={e => setEditData(p => ({ ...p, issueDate: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Expiry Date</Label>
                    <Input type="date" value={editData.expirationDate?.split('T')[0] ?? ''} onChange={e => setEditData(p => ({ ...p, expirationDate: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Status</Label>
                    <Select value={editData.status ?? 'Active'} onValueChange={v => setEditData(p => ({ ...p, status: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="Expired">Expired</SelectItem>
                        <SelectItem value="Suspended">Suspended</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-sm text-muted-foreground">License Number</p><p className="font-medium">{selectedLicense.licenseNumber}</p></div>
                  <div><p className="text-sm text-muted-foreground">Type</p><p className="font-medium">{selectedLicense.licenseType}</p></div>
                  <div><p className="text-sm text-muted-foreground">Operator</p><p className="font-medium">{selectedLicense.operatorName}</p></div>
                  <div><p className="text-sm text-muted-foreground">State</p><p className="font-medium">{selectedLicense.state}</p></div>
                  <div><p className="text-sm text-muted-foreground">Issue Date</p><p className="font-medium">{formatDate(selectedLicense.issueDate)}</p></div>
                  <div><p className="text-sm text-muted-foreground">Expiry Date</p><p className="font-medium">{formatDate(selectedLicense.expirationDate)}</p></div>
                  <div><p className="text-sm text-muted-foreground">Days Until Expiry</p><p className="font-medium">
                    {(() => { const d = getDaysUntilExpiry(selectedLicense.expirationDate); return d === null ? 'N/A' : d > 0 ? `${d} days` : 'Expired'; })()}
                  </p></div>
                  <div><p className="text-sm text-muted-foreground">Status</p><div className="mt-1">{getStatusBadge(selectedLicense)}</div></div>
                  {selectedLicense.notes && (
                    <div className="col-span-2"><p className="text-sm text-muted-foreground">Notes</p><p className="font-medium">{selectedLicense.notes}</p></div>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-2 border-t">
                {isEditMode ? (
                  <>
                    <Button className="flex-1" onClick={handleUpdate} disabled={isSaving}>
                      {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Save Changes
                    </Button>
                    <Button variant="outline" className="flex-1" onClick={() => setIsEditMode(false)}>Cancel</Button>
                  </>
                ) : (
                  <>
                    <Button className="flex-1" onClick={() => setIsEditMode(true)}>Edit License</Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(selectedLicense.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create License Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New License</DialogTitle>
            <DialogDescription>Enter the license details below</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>License Number *</Label>
                <Input required value={newLicense.licenseNumber} onChange={e => setNewLicense(p => ({ ...p, licenseNumber: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>License Type *</Label>
                <Input required value={newLicense.licenseType} onChange={e => setNewLicense(p => ({ ...p, licenseType: e.target.value }))} placeholder="e.g. Cultivation, Dispensary" />
              </div>
              <div className="space-y-1">
                <Label>Operator Name</Label>
                <Input value={newLicense.operatorName} onChange={e => setNewLicense(p => ({ ...p, operatorName: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>State</Label>
                <Input value={newLicense.state} onChange={e => setNewLicense(p => ({ ...p, state: e.target.value }))} placeholder="e.g. CA, CO, OR" />
              </div>
              <div className="space-y-1">
                <Label>Issue Date</Label>
                <Input type="date" value={newLicense.issueDate} onChange={e => setNewLicense(p => ({ ...p, issueDate: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Expiration Date</Label>
                <Input type="date" value={newLicense.expirationDate} onChange={e => setNewLicense(p => ({ ...p, expirationDate: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <Select value={newLicense.status} onValueChange={v => setNewLicense(p => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Expired">Expired</SelectItem>
                    <SelectItem value="Suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1" disabled={isSaving}>
                {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                Add License
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
